<template>
    <RegularModal ref="modal">
        <!-- Modal Title -->
        <template v-slot:title>
            <span class="has-text-warning has-text-weight-semibold">
                ⚠️ {{ translate("side_effects_modal.event_registration.title") }}
            </span>
        </template>

        <template v-slot:body>
            <div v-if="sideEffectsList.length > 0">
                <!-- Competitor Info -->
                <div class="is-size-5 mb-2">
                    <p>
                        <span class="icon">
                            <i class="fas fa-user"></i>
                        </span>
                        <span class="clarity-mask">{{ registeredCompetitor?.firstName }} {{ registeredCompetitor?.lastName }}</span>
                    </p>
                    <p class="is-size-6">{{ registeredCompetitor?.academy }}</p>
                </div>

                <p class="mb-3">
                    {{ translate("side_effects_modal.event_registration.inconsistencies") }}
                </p>

                <!-- Grouped Side Effects -->
                <div v-for="(effects, category) in groupedSideEffects" :key="category" class="box">
                    <p>
                        <strong class="has-text-info">
                            {{ category }}
                        </strong>
                        <span class="tag is-danger is-light ml-2" v-if="hasCategoryAtLeastOneNonFixableSideEffect(category)">
                            {{ translate("side_effects_modal.event_registration.manual_fix_required") }}
                        </span>
                    </p>

                    <ul class="mt-2">
                        <li
                            v-for="effect in effects"
                            :key="effect.parameterId + '-' + effect.type"
                            :class="{ 'has-text-danger': effect.fixBlocked, 'has-text-warning': effect.canBeIgnored }"
                        >
                            <span class="icon" v-if="effect.canBeIgnored">⚠️</span>
                            <span class="icon" v-else-if="!effect.fixBlocked"> 🛠️ </span>
                            <span class="icon" v-else>⛔</span>
                            {{ describeEffect(effect.type) }}
                        </li>
                    </ul>
                </div>

                <p class="is-size-7 has-text-grey-light mt-4">
                    {{ translate("side_effects_modal.event_registration.fix_issues") }}
                </p>
            </div>

            <div v-else>
                <p>{{ translate("side_effects_modal.event_registration.no_side_effects") }}</p>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons is-right">
                <button class="button" @click="close">
                    {{ translate("common.close") }}
                </button>
                <LoadingButton :is-loading="fixingInProgress" class="button is-primary" @click="fixSideEffects" :disabled="!canAutoFix">
                    {{ translate("side_effects_modal.event_registration.fix_now") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../Common/RegularModal.vue";
    import { useModal } from "../Common/useModal";
    import { ref, computed } from "vue";
    import { EventRegistrationSideEffect, EventRegistrationSideEffectReport, SideEffectType } from "../Common/types";
    import { CompetitorPaymentWithMetadata } from "../Api/payments";
    import { translate } from "../Common/translations";
    import autoFixApi from "../Api/autoFixApi";
    import { successToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import LoadingButton from "../Common/LoadingButton.vue";

    const { modal, open: openModal, close } = useModal();
    const sideEffectsList = ref<EventRegistrationSideEffect[]>([]);
    const registeredCompetitor = ref<CompetitorPaymentWithMetadata | null>(null);
    let eventId = 0;

    function open(report: EventRegistrationSideEffectReport) {
        sideEffectsList.value = report.sideEffects;
        registeredCompetitor.value = report.registeredCompetitor;
        eventId = report.eventId;
        openModal();
    }
    defineExpose({ open });

    const groupedSideEffects = computed(() => {
        const grouped: Record<string, EventRegistrationSideEffect[]> = {};
        for (const effect of sideEffectsList.value) {
            if (!grouped[effect.categoryName]) {
                grouped[effect.categoryName] = [];
            }
            grouped[effect.categoryName].push(effect);
        }
        return grouped;
    });

    function describeEffect(type: SideEffectType): string {
        return translate("side_effects_modal.event_registration.effect." + type);
    }

    function hasCategoryAtLeastOneNonFixableSideEffect(category: string): boolean {
        return sideEffectsList.value.some(effect => effect.categoryName === category && effect.fixBlocked);
    }

    // Only fix effects where fixBlocked = false
    const fixableEffects = computed(() => sideEffectsList.value.filter(effect => !effect.canBeIgnored));
    const nonFixableSideEffects = computed(() => sideEffectsList.value.filter(effect => effect.fixBlocked));
    const canAutoFix = computed(() => nonFixableSideEffects.value.length === 0);

    const fixingInProgress = ref(false);

    function fixSideEffects() {
        fixingInProgress.value = true;

        return autoFixApi
            .fixEventRegistrationSideEffects(eventId, {
                eventId: eventId,
                sideEffects: fixableEffects.value,
            })
            .then(() => {
                successToast(translate("side_effects_modal.fix_applied_successfully"));
                close();
            })
            .catch(handleError)
            .finally(() => {
                fixingInProgress.value = false;
            });
    }
</script>

<style scoped>
    .box {
        margin-bottom: 1rem;
    }
</style>
